import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency';
import type Owner from '@ember/owner';
import type { Store } from 'tio-data';
import type SessionContextService from './session-context.ts';
import {
  queryUserPreferenceSettingsRequest,
  readUserPreferenceSettings,
  upsertUserPreferenceSettingRequest,
} from 'tio-data/requests/user-preference-settings';

/** Where the approval page shows its reference drawer buttons: the bottom action bar or a rail on the right edge. */
export type ReferenceControlsPlacement = 'bottom' | 'right';

const SUBJECT = 'tas-application-approval';
const PREDICATE = 'reference-controls';
const CACHE_KEY = 'tio-tas-approval-reference-controls';

function isPlacement(value: unknown): value is ReferenceControlsPlacement {
  return value === 'bottom' || value === 'right';
}

/**
 * The signed-in approver's approval-page layout, saved as their own user preference so it
 * follows them between browsers. This browser remembers the last placement it saw, so the page
 * paints in place instead of jumping once the server answers; the server copy still wins. With
 * nothing remembered, and whenever the server cannot be read, the buttons stay in the bottom bar.
 */
export default class TasApprovalLayoutService extends Service {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;

  @tracked referenceControls: ReferenceControlsPlacement = 'bottom';

  #ownerId: string | undefined;
  #loaded = false;
  #chosenThisSession = false;
  #lastSave: Promise<unknown> = Promise.resolve();

  constructor(owner: Owner) {
    super(owner);
    this.#syncOwner();
  }

  /**
   * Reads the saved placement once per signed-in approver; a failed read keeps the current
   * placement and retries on the next call. Restartable, so another approver's load (a re-login
   * without an app reload) cancels an in-flight one instead of being dropped.
   */
  load = restartableTask(async () => {
    const ownerId = this.#syncOwner();
    if (this.#loaded || !ownerId) return;
    try {
      const { content } = await this.store.request(
        queryUserPreferenceSettingsRequest({ subject: SUBJECT, predicate: PREDICATE })
      );
      if (ownerId !== this.#currentUserId) return;
      const saved = readUserPreferenceSettings(content).find((row) => row.predicate === PREDICATE)
        ?.value['placement'];
      // A choice made while this read was in flight is newer than whatever the server held.
      if (!this.#chosenThisSession) {
        this.referenceControls = isPlacement(saved) ? saved : 'bottom';
        this.#writeCache(this.referenceControls);
      }
      this.#loaded = true;
    } catch {
      // The default layout is always usable, so a failed read is not worth interrupting the approver.
    }
  });

  /**
   * Moves the buttons at once, then saves the choice; saves run in the order chosen, so the
   * last choice is the one stored. A failed save puts back the placement it replaced.
   * @throws The request error when the save fails, so the caller can say the change was not kept.
   */
  setReferenceControls = async (placement: ReferenceControlsPlacement) => {
    const ownerId = this.#syncOwner();
    const previous = this.referenceControls;
    if (placement === previous) return;
    this.referenceControls = placement;
    this.#writeCache(placement);
    this.#chosenThisSession = true;
    const save = this.#lastSave
      .catch(() => undefined)
      .then(() =>
        this.store.request(
          upsertUserPreferenceSettingRequest({
            subject: SUBJECT,
            predicate: PREDICATE,
            value: { placement },
          })
        )
      );
    this.#lastSave = save;
    try {
      await save;
    } catch (error) {
      if (ownerId === this.#currentUserId) {
        this.referenceControls = previous;
        this.#writeCache(previous);
      }
      throw error;
    }
  };

  get #currentUserId() {
    const id = this.sessionContext.user?.id;
    return id ? String(id) : undefined;
  }

  // The service outlives a sign-out, so each approver starts from their own remembered
  // placement instead of inheriting the previous approver's.
  #syncOwner() {
    const ownerId = this.#currentUserId;
    if (ownerId !== this.#ownerId) {
      this.#ownerId = ownerId;
      this.#loaded = false;
      this.#chosenThisSession = false;
      this.referenceControls = this.#readCache() ?? 'bottom';
    }
    return ownerId;
  }

  // Per user, so a shared browser never paints one approver's layout for another.
  get #cacheKey() {
    const userId = this.#currentUserId;
    return userId ? `${CACHE_KEY}:${userId}` : undefined;
  }

  #readCache() {
    const key = this.#cacheKey;
    if (!key) return undefined;
    try {
      const cached = localStorage.getItem(key);
      return isPlacement(cached) ? cached : undefined;
    } catch {
      return undefined;
    }
  }

  #writeCache(placement: ReferenceControlsPlacement) {
    const key = this.#cacheKey;
    if (!key) return;
    try {
      localStorage.setItem(key, placement);
    } catch {
      // Storage can be unavailable (private browsing); the server copy still applies.
    }
  }
}
